import React from 'react';
import TopBar from './chrome/TopBar';

/**
 * The top bar on its own, for pages that bring their own header instead of the
 * banner-and-nav shell (`components/chrome/PageShell`) — dashboards, wallet,
 * settings, admin, and the rest. It renders `components/chrome/TopBar`, which is
 * where the bar itself lives.
 *
 * `mode`/`onModeChange` are accepted and ignored: the guest/host ModeSwitch was
 * retired in v6 and the bar has no mode.
 * docs/plans/2026-08-handoff-layout/01-foundation-chrome.md
 *
 * ModeSwitch / Breadcrumb / PageHeader below are unrelated helpers several pages
 * import from this module.
 */
export default function Navbar({ user, walletBalance, onLogout, onLogoClick, className = '', variant }) {
  return (
    <TopBar
      user={user}
      walletBalance={walletBalance}
      onLogout={onLogout}
      onLogoClick={onLogoClick}
      className={className}
      variant={variant}
    />
  );
}

/**
 * Mode switch component (Find Your Party / Find Your People toggle)
 */
export function ModeSwitch({
  mode,
  onChange,
  fullWidth = false,
  className = '',
}) {
  return (
    <div className={`inline-flex border-2 border-black ${fullWidth ? 'w-full' : ''} ${className}`}>
      <button
        type="button"
        onClick={() => onChange('featured')}
        className={`
          flex-1 px-6 py-2
          font-mono text-sm font-semibold uppercase tracking-wider
          transition-colors duration-200
          ${mode === 'featured'
            ? 'bg-black text-white'
            : 'bg-white text-black hover:bg-gray-100'
          }
        `}
      >
        EVENTS
      </button>
      <button
        type="button"
        onClick={() => onChange('guest')}
        className={`
          flex-1 px-6 py-2
          border-l-2 border-black
          font-mono text-sm font-semibold uppercase tracking-wider
          transition-colors duration-200
          ${mode === 'guest'
            ? 'bg-black text-white'
            : 'bg-white text-black hover:bg-gray-100'
          }
        `}
      >
        GUEST
      </button>
      <button
        type="button"
        onClick={() => onChange('host')}
        className={`
          flex-1 px-6 py-2
          border-l-2 border-black
          font-mono text-sm font-semibold uppercase tracking-wider
          transition-colors duration-200
          ${mode === 'host'
            ? 'bg-black text-white'
            : 'bg-white text-black hover:bg-gray-100'
          }
        `}
      >
        HOST
      </button>
    </div>
  );
}

/**
 * Breadcrumb navigation component
 */
export function Breadcrumb({
  items,
  className = '',
}) {
  return (
    <nav className={`flex items-center gap-2 ${className}`}>
      {items.map((item, index) => (
        <React.Fragment key={index}>
          {index > 0 && (
            <span className="font-mono text-gray-400">/</span>
          )}
          {item.href || item.onClick ? (
            <button
              type="button"
              onClick={item.onClick}
              className="font-mono text-sm uppercase tracking-wider text-gray-600 hover:text-black transition-colors"
            >
              {item.label}
            </button>
          ) : (
            <span className="font-mono text-sm uppercase tracking-wider text-black font-semibold">
              {item.label}
            </span>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
}

/**
 * Page header component
 */
export function PageHeader({
  title,
  subtitle,
  actions,
  breadcrumbs,
  className = '',
}) {
  return (
    <div className={`border-b-2 border-black bg-white px-6 py-6 ${className}`}>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <Breadcrumb items={breadcrumbs} className="mb-4" />
      )}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-mono text-2xl font-bold uppercase tracking-wider text-black">
            {title}
          </h1>
          {subtitle && (
            <p className="font-mono text-sm text-gray-600 mt-1">
              {subtitle}
            </p>
          )}
        </div>
        {actions && (
          <div className="flex items-center gap-3 flex-shrink-0">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}
